import { Injectable } from '@angular/core';
import { Router, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap, take, timeout, catchError } from 'rxjs/operators';

import { AuthenticationService } from '../_services/authentication.service';
import { UserService } from 'src/app/_share/_services/user.service';
import { AuthorizationService } from '../_services/authorization.service';

/**
 * Gates on PERMISSIONS only (route.data['requiredPermission']), never role
 * names - which roles get which permission is a backend
 * system.roles_permissions assignment, not something this file should know
 * about. A route with no requiredPermission is open to any authenticated
 * user (e.g. Home, Settings).
 */
@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private authorizationService: AuthorizationService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const user = this.authenticationService.userValue;

    if (!user) {
      this.router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
      return of(false);
    }

    const requiredPermission: string | string[] | undefined = route.data['requiredPermission'];
    if (!requiredPermission) {
      return of(true);
    }

    return this.userService.currentUser$.pipe(
      take(1),
      switchMap((currentUser: unknown) => {
        const hasData = !!currentUser && Object.keys(currentUser as object).length > 0;
        if (hasData) {
          return of(currentUser);
        }
        // User data not loaded yet (e.g. a hard refresh landed directly on
        // a guarded route) - trigger the load and wait for the real
        // emission, rather than allowing through blind or evaluating
        // permissions against an empty user.
        this.userService.getCurrentLoggedInUser();
        return this.userService.currentUser$.pipe(
          filter((u: unknown) => !!u && Object.keys(u as object).length > 0),
          take(1)
        );
      }),
      switchMap(() =>
        this.authorizationService.ready$.pipe(
          take(1),
          map(() => {
            const perms = Array.isArray(requiredPermission)
              ? requiredPermission
              : [requiredPermission];
            if (!this.authorizationService.hasAnyPermission(perms)) {
              this.router.navigate(['/home']);
              return false;
            }
            return true;
          })
        )
      ),
      timeout(15000),
      catchError(() => {
        // /users/me (or the permission load) never resolved - the session
        // is in an unknown state, so fail closed rather than hang the
        // spinner forever (the exact original login-stall failure mode).
        this.router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
        return of(false);
      })
    );
  }
}
